import { useCallback } from "react";
import { useField, FieldConfig } from "formik";
import { useTranslation } from "react-i18next";
import locale from "../../constants/locale";
import clsx from "clsx";
import { TFunctionResult } from "i18next";

export const FileField = ({
  children,
  onChange,
  ...props
}: FieldConfig<File | File[]> & {
  disabled?: boolean;
  multiple?: boolean;
  onChange?: (v: File | File[]) => void;
  label?: React.ReactChild | TFunctionResult;
  accept?: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const [, { value, touched, error }, { setValue }] = useField(props);
  
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.currentTarget.files) return;
      
      if (props.multiple) {
        // Convert FileList to array for easier handling
        const files = Array.from(e.currentTarget.files);
        setValue(files);
        if (onChange) {
          onChange(files);
        }
      } else {
        // Handle single file as before
        const file = e.currentTarget.files[0];
        setValue(file);
        if (onChange) {
          onChange(file);
        }
      }
    },
    [setValue, onChange, props.multiple]
  );
  
  const label = typeof props.label === "function" ? props.label() : props.label;
  
  // Display logic for file names
  const getFileDisplay = () => {
    if (!value) return t(locale.file);
    
    if (Array.isArray(value)) {
      if (value.length === 0) return t(locale.file);
      if (value.length === 1) return value[0].name;
      return `${value.length} ${t(locale.filesSelected)}`;
    }
    
    return value.name;
  };
  
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div className="file has-name">
        <label className={clsx("file-label", props.className)}>
          <input
            value=""
            onChange={handleChange}
            type="file"
            className="file-input"
            accept={props.accept}
            disabled={props.disabled}
            multiple={props.multiple}
          />
          <span className={clsx("file-cta", props.className)}>
            <span className="file-icon">
              <i className="fas fa-upload"></i>
            </span>
            <span className="file-label has-ellipsis-text">
              {getFileDisplay()}
            </span>
          </span>
        </label>
      </div>
      {touched && !!error && <p className="help is-danger">{error}</p>}
    </div>
  );
};